import { mediaTypeFromFileName } from "../../shared/fileMediaTypes";

export type OfficePreviewFormat = "docx" | "xlsx" | "pptx";

const formats = new Map<string, OfficePreviewFormat>([
  [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "docx",
  ],
  ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "xlsx"],
  [
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "pptx",
  ],
]);

export function officePreviewFormat(contentType: string, fileName?: string) {
  const format = formats.get(contentType);
  if (format) return format;
  if (
    fileName &&
    (contentType === "application/octet-stream" ||
      contentType === "application/zip" ||
      contentType === "application/x-zip-compressed")
  ) {
    return formats.get(mediaTypeFromFileName(fileName));
  }
  return undefined;
}

export const MAX_OFFICE_PREVIEW_BYTES = 25 * 1024 * 1024;

export async function readOfficePreview(
  response: Response
): Promise<ArrayBuffer | null> {
  if (!response.ok)
    throw new Error(`Office file request failed: ${response.status}`);
  if (
    Number(response.headers.get("Content-Length")) > MAX_OFFICE_PREVIEW_BYTES
  ) {
    await response.body?.cancel();
    return null;
  }
  if (!response.body) throw new Error("Empty Office file response");
  const reader = response.body.getReader();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  let size = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > MAX_OFFICE_PREVIEW_BYTES) {
        await reader.cancel();
        return null;
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const buffer = await new Blob(chunks).arrayBuffer();
  const signature = new Uint8Array(buffer, 0, Math.min(4, buffer.byteLength));
  if (
    signature[0] !== 0x50 ||
    signature[1] !== 0x4b ||
    signature[2] !== 3 ||
    signature[3] !== 4
  ) {
    throw new Error("Office preview requires an OOXML ZIP file");
  }
  return buffer;
}
